import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
  AlertColor,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import axiosInstance from '../../../../utils/axios';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { routes } from '../../../../utils/constants/routes';
import { URLS } from '../../../../utils/constants/urls';
import { priviewLink } from '../../../../utils/redux/reducer/authentication-slice';
import { filterDataforApi, pageNo, rowCount, setPageNo } from '../../../../utils/redux/reducer/filter-slice';
import { createDropdownData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { VenueConfigurationHealthCell } from './VenueConfigurationHealthPanel';

type DropdownOption = { label: string; value: number | string };

type VenueConfigurationRow = {
  id: number;
  venueName?: string;
  eventTypeName?: string;
  maximumBookCapacity?: number;
  packagesConfigured?: number;
  completionPercentage?: number;
  configurationHealth?: { label: string; complete: boolean }[];
  missingFields?: string[];
  editedAt?: string | null;
  editedBy?: string | null;
};

type LocalFilterState = {
  venueId: string;
  eventTypeId: string;
  pricePackageId: string;
  layoutId: string;
  availability: string;
};

const LOCAL_FILTER_KEYS = ['venueId', 'eventTypeId', 'pricePackageId', 'layoutId', 'isAvailable'] as const;

const compactFilterSelectSx = {
  height: 36,
  fontSize: 13,
  bgcolor: '#fff',
  '& .MuiSelect-select': { py: 0.75 },
};

const formatCapacity = (capacity?: number) => {
  if (capacity == null || capacity <= 0) return '—';
  return `Up to ${capacity}`;
};

const cleanFilterAnds = (items: string[]) => {
  const cleaned: string[] = [];
  items.forEach(item => {
    if (item === 'and') {
      if (cleaned.length === 0 || cleaned[cleaned.length - 1] === 'and') return;
      cleaned.push(item);
      return;
    }
    cleaned.push(item);
  });
  if (cleaned.length && cleaned[cleaned.length - 1] === 'and') cleaned.pop();
  return cleaned;
};

const withLocalFilters = (filter: unknown, local: LocalFilterState) => {
  const base = Array.isArray(filter)
    ? filter.filter(
        (item: unknown) =>
          typeof item !== 'string' ||
          item === 'and' ||
          !LOCAL_FILTER_KEYS.some(key => (item as string).startsWith(`${key}:`)),
      )
    : [];

  const cleaned = cleanFilterAnds(base as string[]);
  const extras: string[] = [];
  if (local.venueId) extras.push(`venueId:eq:${local.venueId}`);
  if (local.eventTypeId) extras.push(`eventTypeId:eq:${local.eventTypeId}`);
  if (local.pricePackageId) extras.push(`pricePackageId:eq:${local.pricePackageId}`);
  if (local.layoutId) extras.push(`layoutId:eq:${local.layoutId}`);
  if (local.availability) extras.push(`isAvailable:cic:${local.availability}`);

  extras.forEach(extra => {
    if (cleaned.length) cleaned.push('and');
    cleaned.push(extra);
  });
  return cleaned;
};

const VenueConfigurationList: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const previewLink = useSelector(priviewLink) as string | undefined;

  const [pageResponse, setPageResponse] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [venues, setVenues] = useState<DropdownOption[]>([]);
  const [eventTypes, setEventTypes] = useState<DropdownOption[]>([]);
  const [pricePackages, setPricePackages] = useState<DropdownOption[]>([]);
  const [layouts, setLayouts] = useState<DropdownOption[]>([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [selectedEventTypeId, setSelectedEventTypeId] = useState(() => searchParams.get('eventTypeId') || '');
  const [selectedPricePackageId, setSelectedPricePackageId] = useState(
    () => searchParams.get('pricePackageId') || '',
  );
  const [selectedLayoutId, setSelectedLayoutId] = useState(() => searchParams.get('layoutId') || '');
  const [selectedAvailability, setSelectedAvailability] = useState('');
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({ display: false, severity: null, message: '' });

  const localFilters = useMemo(
    (): LocalFilterState => ({
      venueId: selectedVenueId,
      eventTypeId: selectedEventTypeId,
      pricePackageId: selectedPricePackageId,
      layoutId: selectedLayoutId,
      availability: selectedAvailability,
    }),
    [selectedAvailability, selectedEventTypeId, selectedLayoutId, selectedPricePackageId, selectedVenueId],
  );

  useEffect(() => {
    const eventTypeIdFromUrl = searchParams.get('eventTypeId') || '';
    const pricePackageIdFromUrl = searchParams.get('pricePackageId') || '';
    const layoutIdFromUrl = searchParams.get('layoutId') || '';
    setSelectedEventTypeId(prev => (prev === eventTypeIdFromUrl ? prev : eventTypeIdFromUrl));
    setSelectedPricePackageId(prev => (prev === pricePackageIdFromUrl ? prev : pricePackageIdFromUrl));
    setSelectedLayoutId(prev => (prev === layoutIdFromUrl ? prev : layoutIdFromUrl));
  }, [searchParams]);

  useEffect(() => {
    const fetchFilterDropdowns = async () => {
      try {
        const [{ data }, { data: allEventTypes }] = await Promise.all([
          axiosInstance.get(URLS.venueConfigDropdownData),
          axiosInstance.get(URLS.eventTypesDropdown),
        ]);
        setVenues(createDropdownData(data?.venues, ['id', 'name']) || []);
        setEventTypes(createDropdownData(allEventTypes, ['id', 'name']) || []);
        setPricePackages(createDropdownData(data?.pricePackages, ['id', 'name']) || []);
        setLayouts(createDropdownData(data?.layouts, ['id', 'name']) || []);
      } catch (e: any) {
        setMessage({
          display: true,
          severity: 'error',
          message: e.response?.data?.message ?? e.message,
        });
      }
    };
    void fetchFilterDropdowns();
  }, []);

  const resetToFirstPage = useCallback(() => {
    dispatch(setPageNo(0));
  }, [dispatch]);

  const updateSearchParam = (key: string, value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value) nextParams.set(key, value);
    else nextParams.delete(key);
    setSearchParams(nextParams, { replace: true });
  };

  const handleVenueFilterChange = (value: string) => {
    setSelectedVenueId(value);
    resetToFirstPage();
  };

  const handleEventTypeFilterChange = (value: string) => {
    setSelectedEventTypeId(value);
    updateSearchParam('eventTypeId', value);
    resetToFirstPage();
  };

  const handlePricePackageFilterChange = (value: string) => {
    setSelectedPricePackageId(value);
    updateSearchParam('pricePackageId', value);
    resetToFirstPage();
  };

  const handleLayoutFilterChange = (value: string) => {
    setSelectedLayoutId(value);
    updateSearchParam('layoutId', value);
    resetToFirstPage();
  };

  const handleAvailabilityFilterChange = (value: string) => {
    setSelectedAvailability(value);
    resetToFirstPage();
  };

  const handleCopy = useCallback(
    async (id: number) => {
      try {
        setLoading(true);
        const { status, data } = await axiosInstance.get(URLS.venueConfigCopy(id));
        if (status === 200) {
          setMessage({
            display: true,
            severity: 'success',
            message: strings.copied_successfully_text,
          });
          setTimeout(() => {
            navigate(`${routes.editVenueConfiguration}/${data.id}?type=copy`);
          }, 1000);
        }
      } catch (e: any) {
        setMessage({
          display: true,
          severity: 'error',
          message: e.response?.data?.message || e.message,
        });
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const getList = useCallback(
    async (filterData: PageProps) => {
      try {
        setLoading(true);
        const body = filterData
          ? {
              ...filterData,
              filter: withLocalFilters(filterData.filter, localFilters),
            }
          : {
              page: index + 1,
              size: pageSize,
              filter: withLocalFilters(filter, localFilters),
              sort: ['createdAt:desc'],
            };
        const { data } = await axiosInstance.post(URLS.venueConfiguration, body);
        setPageResponse(data);
      } catch (error: any) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response?.data?.message || error.message,
        });
      } finally {
        setLoading(false);
      }
    },
    [index, pageSize, filter, localFilters],
  );

  useEffect(() => {
    void getList(null as any);
  }, [getList]);

  useEffect(() => {
    const state = location.state as { bulkCreateMessage?: string; bulkCreateSeverity?: AlertColor } | null;
    if (state?.bulkCreateMessage) {
      setMessage({
        display: true,
        severity: state.bulkCreateSeverity ?? 'success',
        message: state.bulkCreateMessage,
      });
      void getList(null as any);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, getList]);

  const pageConfig = useMemo(
    () => ({
      title: strings.venueConfig_title_list,
      subtitle: 'Manage venue configurations by venue, segment, package, and layout.',
      entityLabel: 'Configurations',
      AddButtonText: 'Add Configurations',
      AddButtonUrl: routes.bulkCreateVenueConfiguration,
      customAdd: () => navigate(routes.bulkCreateVenueConfiguration),
      editButtonUrl: routes.editVenueConfiguration,
      listApi: URLS.venueConfiguration,
      deleteApi: URLS.get_edit_delete_VenueConfigurationById,
      addPrivilege: privileges.add_venue_configuration,
      editPrivilege: privileges.edit_venue_configuration,
      deletePrivilege: privileges.delete_venue_configuration,
      inlineCustomActions: true,
      customImage: {
        materialIcon: <ContentCopyOutlinedIcon style={{ fontSize: 20 }} />,
        buttonText: strings.copy,
        privilege: privileges.add_venue_configuration,
        handleCustomImageMethod: (id: number) => {
          void handleCopy(id);
        },
      },
      renderKpis: previewLink
        ? () => (
            <Link color="secondary" target="_blank" rel="noopener" variant="body2" href={previewLink}>
              {strings.bookings_button_preview}
            </Link>
          )
        : undefined,
      headerFilters: () => (
        <>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel sx={{ fontSize: 12 }}>Venue</InputLabel>
            <Select
              label="Venue"
              value={selectedVenueId}
              onChange={event => handleVenueFilterChange(String(event.target.value))}
              sx={compactFilterSelectSx}>
              <MenuItem value="">All venues</MenuItem>
              {venues.map(option => (
                <MenuItem key={option.value} value={String(option.value)} sx={{ fontSize: 13 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel sx={{ fontSize: 12 }}>Segment</InputLabel>
            <Select
              label="Segment"
              value={selectedEventTypeId}
              onChange={event => handleEventTypeFilterChange(String(event.target.value))}
              sx={compactFilterSelectSx}>
              <MenuItem value="">All segments</MenuItem>
              {eventTypes.map(option => (
                <MenuItem key={option.value} value={String(option.value)} sx={{ fontSize: 13 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <InputLabel sx={{ fontSize: 12 }}>Price package</InputLabel>
            <Select
              label="Price package"
              value={selectedPricePackageId}
              onChange={event => handlePricePackageFilterChange(String(event.target.value))}
              sx={compactFilterSelectSx}>
              <MenuItem value="">All price packages</MenuItem>
              {pricePackages.map(option => (
                <MenuItem key={option.value} value={String(option.value)} sx={{ fontSize: 13 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel sx={{ fontSize: 12 }}>Layout</InputLabel>
            <Select
              label="Layout"
              value={selectedLayoutId}
              onChange={event => handleLayoutFilterChange(String(event.target.value))}
              sx={compactFilterSelectSx}>
              <MenuItem value="">All layouts</MenuItem>
              {layouts.map(option => (
                <MenuItem key={option.value} value={String(option.value)} sx={{ fontSize: 13 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
            <InputLabel sx={{ fontSize: 12 }}>Status</InputLabel>
            <Select
              label="Status"
              value={selectedAvailability}
              onChange={event => handleAvailabilityFilterChange(String(event.target.value))}
              sx={compactFilterSelectSx}>
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="true" sx={{ fontSize: 13 }}>
                {strings.venue_column_available}
              </MenuItem>
              <MenuItem value="false" sx={{ fontSize: 13 }}>
                {strings.venue_column_not_available || 'Not Available'}
              </MenuItem>
            </Select>
          </FormControl>
        </>
      ),
      filterChips: [
        ...(selectedVenueId
          ? [
              {
                key: 'venueId',
                label: `Venue : ${
                  venues.find(option => String(option.value) === selectedVenueId)?.label || selectedVenueId
                }`,
              },
            ]
          : []),
        ...(selectedEventTypeId
          ? [
              {
                key: 'eventTypeId',
                label: `Segment : ${
                  eventTypes.find(option => String(option.value) === selectedEventTypeId)?.label ||
                  selectedEventTypeId
                }`,
              },
            ]
          : []),
        ...(selectedPricePackageId
          ? [
              {
                key: 'pricePackageId',
                label: `Price package : ${
                  pricePackages.find(option => String(option.value) === selectedPricePackageId)?.label ||
                  selectedPricePackageId
                }`,
              },
            ]
          : []),
        ...(selectedLayoutId
          ? [
              {
                key: 'layoutId',
                label: `Layout : ${
                  layouts.find(option => String(option.value) === selectedLayoutId)?.label || selectedLayoutId
                }`,
              },
            ]
          : []),
        ...(selectedAvailability
          ? [
              {
                key: 'isAvailable',
                label: `Status : ${
                  selectedAvailability === 'true'
                    ? strings.venue_column_available
                    : strings.venue_column_not_available || 'Not Available'
                }`,
              },
            ]
          : []),
      ],
      onClearFilterChip: (key: string) => {
        if (key === 'venueId') handleVenueFilterChange('');
        if (key === 'eventTypeId') handleEventTypeFilterChange('');
        if (key === 'pricePackageId') handlePricePackageFilterChange('');
        if (key === 'layoutId') handleLayoutFilterChange('');
        if (key === 'isAvailable') handleAvailabilityFilterChange('');
      },
      onClearAllFilterChips: () => {
        handleVenueFilterChange('');
        handleEventTypeFilterChange('');
        handlePricePackageFilterChange('');
        handleLayoutFilterChange('');
        handleAvailabilityFilterChange('');
      },
      tableColumn: [
        {
          field: 'venueName',
          headerName: strings.venueConfig_column_venue,
          flex: 1.4,
          sortable: true,
          localField: 'venue.localizedAttributes.name',
          renderCell: (value: any) => (
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#101828' }}>{value || '—'}</Typography>
          ),
        },
        {
          field: 'eventTypeName',
          headerName: strings.venueConfig_column_segment,
          flex: 1.2,
          sortable: true,
          localField: 'eventType.localizedAttributes.name',
          renderCell: (value: any) => (
            <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: '#101828' }}>{value || '—'}</Typography>
          ),
        },
        {
          field: 'maximumBookCapacity',
          headerName: 'Capacity',
          flex: 0.9,
          sortable: true,
          renderCell: (value: any) => (
            <Typography sx={{ fontSize: 13, color: '#344054' }}>{formatCapacity(value)}</Typography>
          ),
        },
        {
          field: 'packagesConfigured',
          headerName: 'Packages Configured',
          flex: 1,
          sortable: false,
          renderCell: (value: any) => (
            <Typography sx={{ fontSize: 13, color: '#344054' }}>{value ?? 0}</Typography>
          ),
        },
        {
          field: 'completionPercentage',
          headerName: strings.venueConfig_column_health,
          flex: 0.9,
          sortable: false,
          textAlign: 'center',
          renderCell: (_value: any, row: VenueConfigurationRow) => (
            <VenueConfigurationHealthCell
              value={row.completionPercentage ?? 0}
              configurationHealth={row.configurationHealth}
              missingFields={row.missingFields}
            />
          ),
        },
        lastUpdatedTableColumn(),
      ],
    }),
    [
      navigate,
      handleCopy,
      previewLink,
      selectedVenueId,
      selectedEventTypeId,
      selectedPricePackageId,
      selectedLayoutId,
      selectedAvailability,
      venues,
      eventTypes,
      pricePackages,
      layouts,
    ],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={value => setMessage(prev => ({ ...prev, display: value }))}
        />
      )}
      <ListPage isLoading={loading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />
    </>
  );
};

export default VenueConfigurationList;
